import * as React from 'react'
import { motion } from 'motion/react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { CardAction } from './card-action'

type BookThemeCardSize = 'large' | 'small'

const sizeStyles: Record<BookThemeCardSize, { size: string; img: number; maxW: string }> = {
  large: { size: 'srd:size-32', img: 128, maxW: 'srd:max-w-28' },
  small: { size: 'srd:w-[104px] srd:h-[104px]', img: 104, maxW: 'srd:max-w-[88px]' },
}

interface BookThemeCardProps {
  id: string
  name: string
  iconUrl?: string
  action: CardAction
  size?: BookThemeCardSize
  className?: string
}

export function BookThemeCard({ id, name, iconUrl, action, size = 'large', className }: BookThemeCardProps) {
  const { size: sizeClass, img, maxW } = sizeStyles[size]

  const content = (
    <motion.div
      data-id={id}
      whileHover={{ scale: 1.025 }}
      whileTap={{ scale: 0.975 }}
      className={cn('srd:cursor-pointer srd:relative srd:rounded-2xl srd:overflow-clip', sizeClass, className)}
    >
      <div className={cn('srd:bg-grey-10 srd:flex srd:items-center srd:justify-center srd:w-full srd:h-full')}>
        {iconUrl && (
          <img
            src={iconUrl}
            alt={name}
            width={img}
            height={img}
            draggable={false}
            className='srd:w-full srd:h-full srd:object-cover'
          />
        )}
      </div>

      <div className='srd:absolute srd:inset-0 srd:bg-linear-to-b srd:from-navy-100/0 srd:to-navy-100' />

      <span
        className={cn(
          typographyVariants({ variant: 'label-sm' }),
          'srd:text-center srd:text-neutral-white srd:line-clamp-2 srd:absolute srd:bottom-2 srd:inset-x-0 srd:mx-auto',
          maxW,
        )}
      >
        {name}
      </span>
    </motion.div>
  )

  if (action.type === 'link') return action.renderWrapper(content)

  return (
    <button type='button' onClick={action.onClick} className='srd:block'>
      {content}
    </button>
  )
}
